<script setup lang="ts">
import { Toaster } from '@datagouv/components-next'

import config from '@/config'

import { DsfrFooter } from '@gouvminint/vue-dsfr'
import HeaderComponent from './components/header/HeaderComponent.vue'
import type { InfoToAnnounce } from './components/LiveRegion.vue'
import LiveRegion from './components/LiveRegion.vue'
import SkipLinks, { type SkipLinksProps } from './components/SkipLinks.vue'
import {
  AccessibilityPropertiesKey,
  type AccessibilityPropertiesType
} from './model/injectionKeys'
import { useUserStore } from './store/UserStore'
import { fromMarkdown } from './utils'
import { useCmsAccess } from './utils/cmsAccess'
import { useWebsiteConfig } from './utils/config'

const userStore = useUserStore()
const route = useRoute()
const isNoticeClosed = ref(false)

// only shows the shortcut to users who'll actually be let into the admin route
const canManageCms = useCmsAccess()

const skipLinks: SkipLinksProps['links'] = [
  {
    id: 'main-content',
    text: 'Aller au contenu'
  },
  {
    id: 'main-nav',
    text: 'Aller au menu principal'
  }
]
const liveInfos: Ref<InfoToAnnounce[] | undefined> = ref()

const noticeContent = computed(() => {
  if (!config.website.notice?.display) return
  return fromMarkdown(config.website.notice?.content, true).html
})

const siteID = config.site_id
const isLoggedIn = computed(() => userStore.$state.isLoggedIn)

const userName = computed(() => userStore.userName)
const quickLinks = computed(() => {
  const headerButton = config.website.header.button.display
    ? {
        label: config.website.header.button.label,
        icon: 'fr-icon-lightbulb-line',
        href: config.website.header.button.link,
        iconRight: true
      }
    : null

  const adminShorcut =
    config.website.header.admin_shortcut && isLoggedIn.value
      ? {
          label: 'Administration',
          icon: 'fr-icon-settings-5-line',
          href: `${config.datagouvfr.base_url}/admin`,
          iconRight: true
        }
      : null

  const cmsShortcut =
    config.website.cms?.enabled && canManageCms.value
      ? {
          label: 'CMS',
          icon: 'fr-icon-article-line',
          to: '/admin/cms',
          iconRight: true
        }
      : null

  const userProfile = isLoggedIn.value
    ? {
        button: true,
        disabled: true,
        label: userName.value,
        icon: 'fr-icon-account-circle-line',
        iconRight: true
      }
    : null

  const logLink = config.website.oauth_option
    ? {
        label: isLoggedIn.value ? 'Déconnexion' : 'Se connecter',
        icon: isLoggedIn.value
          ? 'fr-icon-logout-box-r-line'
          : 'fr-icon-account-circle-line',
        to: isLoggedIn.value ? '/logout' : '/login',
        iconRight: true
      }
    : null

  const buttons = [
    userProfile,
    headerButton,
    cmsShortcut,
    adminShorcut,
    logLink
  ]

  return buttons.filter((button) => button !== null)
})

onMounted(() => {
  userStore.init()
})

const { footer, rf_title, title } = useWebsiteConfig()
const { logo, phrase, external_links, mandatory_links } = footer

const skipLinksComp =
  useTemplateRef<InstanceType<typeof SkipLinks>>('skipLinksComp')

const setAccessibilityProperties: AccessibilityPropertiesType = (
  title,
  focus = true,
  messages = []
): void => {
  // announce page title to screen reader
  if (title) {
    liveInfos.value = [
      { text: `Page ${title} | ${config.website.title}` },
      ...messages
    ]
  }
  // focus skip link container
  if (focus && skipLinksComp.value?.skipLinkList) {
    skipLinksComp.value.skipLinkList.focus()
  }
}

provide(AccessibilityPropertiesKey, setAccessibilityProperties)
</script>

<template>
  <Toaster rich-colors />
  <div id="tooltips" />
  <!-- fullscreen mode is responsible for its own skiplinks -->
  <SkipLinks
    v-if="!route.meta.fullscreen"
    ref="skipLinksComp"
    :links="skipLinks"
  />
  <LiveRegion v-if="liveInfos" :infos="liveInfos" aria-live-mode="assertive" />
  <DsfrNotice
    v-if="!route.meta.fullscreen && !isNoticeClosed && noticeContent"
    :closeable="config.website.notice?.closeable ? true : undefined"
    @close="isNoticeClosed = true"
  >
    <!-- eslint-disable-next-line vue/no-v-html -->
    <span v-html="noticeContent"></span>
  </DsfrNotice>
  <HeaderComponent
    v-if="!route.meta.fullscreen"
    :user-name="userName"
    :quick-links="quickLinks"
    :custom-search="true"
  />

  <main id="main-content" :class="siteID" role="main">
    <RouterView />
  </main>

  <DsfrFooter
    v-if="!route.meta.fullscreen"
    :class="[siteID, 'fr-mt-16w']"
    :logo-text="rf_title"
    :operator-img-src="logo?.src"
    :operator-img-style="{
      height: logo?.height,
      width: logo?.width
    }"
    :desc-text="phrase"
    :ecosystem-links="external_links"
    :mandatory-links="mandatory_links"
    :home-title="`Retour à l'accueil du site - ${title}`"
  />
</template>

<!-- global styles -->
<style>
.es__tiles__list {
  list-style-type: none;
}
.justify-end {
  justify-content: flex-end;
}

.fr-footer__brand-link {
  box-sizing: content-box;
}
.fr-footer__logo {
  max-width: 100%;
}
.culture.fr-mt-16w {
  margin-top: 0 !important;
}
</style>
